import { ButtonHTMLAttributes, ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/utils/cn";

export type NormalButtonSize = "sm" | "md" | "lg";

export interface NormalButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Border color class (e.g., "border-gray-300", "border-green-200") */
  borderColor?: string;
  /** Background color class (e.g., "bg-white", "bg-green-50") */
  bgColor?: string;
  /** Text color class (e.g., "text-gray-700", "text-green-600") */
  textColor?: string;
  /** Hover background color class (e.g., "hover:bg-gray-50") */
  hoverBgColor?: string;
  /** Button size */
  size?: NormalButtonSize;
  /** Loading state */
  isLoading?: boolean;
  /** Icon to show on the left */
  leftIcon?: ReactNode;
  /** Icon to show on the right */
  rightIcon?: ReactNode;
  /** Full width button */
  fullWidth?: boolean;
}

const sizeClasses: Record<NormalButtonSize, string> = {
  sm: "px-3 py-1.5 text-xs",
  md: "px-4 py-2 text-sm",
  lg: "px-5 py-2.5 text-sm",
};

export const NormalButton = ({
  children,
  borderColor = "border-gray-300",
  bgColor = "bg-white",
  textColor = "text-gray-700",
  hoverBgColor = "hover:bg-gray-50",
  size = "sm",
  isLoading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  className,
  disabled,
  ...props
}: NormalButtonProps) => {
  return (
    <button
      className={cn(
        // Base styles
        "inline-flex items-center justify-center rounded-lg border font-medium transition-colors duration-200",
        "focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-300",
        "disabled:pointer-events-none disabled:opacity-50",
        // Custom colors
        borderColor,
        bgColor,
        textColor,
        hoverBgColor,
        // Size
        sizeClasses[size],
        // Width
        fullWidth && "w-full",
        className
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
      {!isLoading && leftIcon && <span className="mr-1.5">{leftIcon}</span>}
      {children}
      {!isLoading && rightIcon && <span className="ml-1.5">{rightIcon}</span>}
    </button>
  );
};

NormalButton.displayName = "NormalButton";

export default NormalButton;
